import dayjs from 'dayjs';
import {
    OpsBriefingResponse,
    EvidenceResponse,
    MembersResponse,
    CoachesResponse,
    HistoryResponse,
    OrderDto,
    CreateOrderRequest,
    UpdateOrderRequest,
    UpdateBriefingItemStatusResponse,
    UpdateMemberVisibilityResponse,
    UpdateCoachVisibilityResponse,
    DateKst,
    MonthCoverageDto,
    OpsBriefingDayState,
} from '../../types/opsBriefing';
import { mockBriefingItems, mockYesterdayOrders } from './mockData';
import { mockRawData } from './rawData';
import {
    mockMemberCards,
    mockCoachRows,
    mockCoachClosing,
    mockConversionFunnel,
    mockDayReports,
    mockMonthlySummary,
    mockMonthlyInsights,
    CENTER_CLOSING,
    COACH_CENTER_AVG,
} from './tabData';
import { mockOrders } from './orderTrackerData';

/**
 * 목 데이터를 API 계약(types/opsBriefing.ts) 모양으로 내보내는 층.
 *
 * ⚠️ 임시입니다. 서버가 열리면 이 파일과 mock* 데이터 파일들을 통째로 지웁니다.
 *
 * 여기서 중요한 것은 "화면이 쓰는 모양"이 아니라 "서버가 줄 모양"으로 맞추는 겁니다.
 * 계산된 값(순번·단계·이탈률 안내)은 빼고 원본만 내보냅니다.
 * 그래야 서버가 붙었을 때 프론트가 똑같이 계산해서 똑같은 결과가 나옵니다.
 */

const MOCK_CENTER_ACTIVE_MEMBERS = 117;

/** axios 가 던지는 모양을 흉내낸다 — 화면의 에러 처리가 목에서도 같은 길을 탄다 */
const httpError = (status: number, message: string) => ({
    response: { status, data: { success: false, message } },
    message,
});

/* ── ① 오늘의 브리핑 ─────────────────────────────────────── */

/**
 * 목 모드에서 원장이 바꾼 항목 상태(가리기·되돌리기).
 *
 * 이걸 안 들고 있으면 가린 항목이 **다음 조회에 그대로 돌아옵니다.**
 * 그러면 목으로 화면을 보는 동안에는 가리기가 고장난 것처럼 보이고,
 * 정작 낙관 반영이 맞는지 아닌지를 개발 중에 확인할 수가 없습니다.
 * 새로고침하면 초기화됩니다 — 서버가 붙으면 사라질 임시 상태입니다.
 *
 * ⚠️ 키가 **`날짜:itemId`** 입니다. itemId 만으로 키를 잡으면 8월 13일에서 가린 것이
 *    8월 14일에서도 가려져 보입니다 — 서버가 `?date=` 를 필수로 만든 이유가
 *    바로 그 혼동인데, 목이 그 혼동을 그대로 갖고 있으면 개발 중에 못 잡습니다.
 */
let briefingItemStatus: Record<string, 'PENDING' | 'ACTED' | 'DISMISSED'> = {};

const statusKey = (date: DateKst, itemId: string) => `${date}:${itemId}`;

export const mockOpsBriefing = (centerId: string, date: DateKst): OpsBriefingResponse => ({
    date,
    centerId,
    activeMembers: MOCK_CENTER_ACTIVE_MEMBERS,
    /*
     * 편성 기록은 서버가 브리핑을 만들 때 굳혀 두는 값이라 목에는 없다.
     * 그럴듯한 숫자를 지어 넣으면 화면이 "기회 3건이 밀렸다" 같은 없는 사실을 말한다.
     * 모르는 것은 null 로 둔다 — 그러면 화면은 편성 문구를 예전처럼 규모로만 설명한다.
     */
    composition: null,
    // 서버는 status 를 갖고 내려준다. 화면에서 바꾼 상태는 프론트가 들고 있는다.
    items: mockBriefingItems.map((it) => ({
        ...it,
        /* 원장이 가린 항목은 목에서도 DISMISSED 로 돌아와야 백필이 실제로 확인된다 */
        status: briefingItemStatus[statusKey(date, it.itemId)] ?? it.status,
        // 지시를 만들 때 필요한 식별자. 목에는 없어서 이름으로 역참조한다.
        subjectId:
            it.subjectType === 'MEMBER'
                ? (mockMemberCards.find((m) => m.name === it.subjectName)?.memberId ?? '')
                : it.subjectType === 'COACH'
                  ? (mockCoachRows.find((c) => c.name === it.subjectName)?.coachId ?? '')
                  : '',
        relatedCoachId: it.relatedCoachName
            ? (mockCoachRows.find((c) => c.name === it.relatedCoachName)?.coachId ?? '')
            : undefined,
    })),
    yesterdayOrders: {
        total: mockYesterdayOrders.length,
        done: mockYesterdayOrders.filter((o) => o.done).length,
    },
});

/**
 * 항목 가리기 / 되돌리기.
 *
 * **서버의 400·409 를 그대로 흉내냅니다.** 목이 더 관대하면 목으로 만든 흐름이
 * 실서버에서 막힙니다 — 화면은 ACTED 항목에 버튼을 아예 안 그리지만,
 * 그 방어가 빠졌을 때 목에서도 똑같이 막혀야 개발 중에 알아챕니다.
 *
 * `date` 가 없으면 400 입니다. 타입에서 이미 필수라 화면 코드로는 못 만드는
 * 상황이지만, 서버가 왜 400 을 주는지가 여기 남아 있어야
 * 나중에 "그냥 오늘로 채우면 되지 않나" 로 되돌리지 않습니다.
 */
export const mockSetBriefingItemStatus = (
    date: DateKst,
    itemId: string,
    status: 'DISMISSED' | 'PENDING'
): UpdateBriefingItemStatusResponse => {
    if (!date) throw httpError(400, '어느 날짜의 브리핑인지(date)가 필요합니다.');
    const item = mockBriefingItems.find((it) => it.itemId === itemId);
    if (!item) throw httpError(404, '해당 브리핑 항목을 찾을 수 없습니다.');

    const key = statusKey(date, itemId);
    const current = briefingItemStatus[key] ?? item.status;
    if (current === 'ACTED') {
        throw httpError(409, '이미 지시를 낸 항목입니다. 가릴 수 없습니다.');
    }
    briefingItemStatus = { ...briefingItemStatus, [key]: status };
    /* 이미 그 상태였으면 아무것도 바뀌지 않았다 — 실패가 아니다 */
    return { briefingDate: date, itemId, status, changed: current !== status };
};

/* ── ② 원천 데이터 ───────────────────────────────────────── */

export const mockEvidence = (itemId: string): EvidenceResponse | null => {
    const raw = mockRawData[itemId];
    if (!raw) return null;
    return { itemId, ...raw };
};

/* ── ③ 회원 현황 ─────────────────────────────────────────── */

/**
 * 목 모드에서 원장이 가려 둔 회원.
 *
 * ⚠️ 키에 **날짜가 없습니다.** 항목 가리기(briefingItemStatus)와 반대입니다 —
 * 회원 가리기는 (centerId, memberId) 한 쌍이고 다시 보기 전까지 영구라
 * 날짜 축이 아예 없습니다. 목이 날짜별로 들고 있으면 개발 중에 "어제 가린 회원이
 * 오늘 돌아오는" 잘못된 동작을 정상으로 오해하게 됩니다.
 */
let hiddenMemberIds = new Set<string>();

/**
 * 회원 가리기 / 다시 보기.
 *
 * 서버와 같이 **멱등**입니다. 이미 그 상태면 `changed: false` 로 200 입니다 —
 * 실패가 아닙니다. 화면이 changed 를 실패로 읽지 않는지 목에서 확인할 수 있어야 합니다.
 */
export const mockSetMemberHidden = (
    memberId: string,
    hidden: boolean
): UpdateMemberVisibilityResponse => {
    const known = mockMemberCards.some((m) => m.memberId === memberId);
    /* 가릴 때만 "이 센터 회원인가"를 본다. 해제는 언제나 열려 있다 (서버와 같다) */
    if (hidden && !known) throw httpError(400, '이 센터의 회원이 아닙니다.');

    const was = hiddenMemberIds.has(memberId);
    const next = new Set(hiddenMemberIds);
    if (hidden) next.add(memberId);
    else next.delete(memberId);
    hiddenMemberIds = next;

    return { memberId, hidden, changed: was !== hidden };
};

export const mockMembers = (date: DateKst): MembersResponse => ({
    date,
    // 순번·단계·이탈률 안내는 서버가 주지 않는다. 프론트가 memberships 로 계산한다.
    members: mockMemberCards.map((m) => ({
        memberId: m.memberId,
        name: m.name,
        coachId: mockCoachRows.find((c) => c.name === m.coachName)?.coachId ?? '',
        coachName: m.coachName,
        product: m.product,
        totalSession: m.totalSession,
        remain: m.remain,
        expiryDday: m.expiryDday,
        memberships: m.memberships,
        paceBefore: m.paceBefore,
        paceNow: m.paceNow,
        paceDeltaPct: m.paceDeltaPct,
        trend: m.trend,
        noshowPct: m.noshowPct,
        /*
         * 안 적어 둔 회원은 0회(재봤더니 0회)다. `?? 0` 을 쓰면 안 된다 —
         * 그러면 일부러 적어 둔 null("못 쟀다")까지 0 으로 눕혀서
         * 화면의 '—' 분기를 목에서 영영 못 본다.
         */
        sameDayAdjustments: m.sameDayAdjustments === undefined ? 0 : m.sameDayAdjustments,
        /* 서버는 가려진 회원도 응답에서 빼지 않는다. 표에서 감추는 것은 화면의 일이다 */
        hidden: hiddenMemberIds.has(m.memberId),
        lastLessonDaysAgo: m.lastLessonDaysAgo,
        // 금액은 서버가 계산하지 않는다. 단가만 준다.
        pricePerSchedule: m.pricePerSchedule,
        totalPrice: m.totalPrice,
    })),
    conversionFunnel: {
        month: mockConversionFunnel.month,
        firstPassEnded: mockConversionFunnel.firstPassEnded,
        consulted: mockConversionFunnel.consulted,
        converted: mockConversionFunnel.converted,
    },
});

/* ── ④ 강사 현황 ─────────────────────────────────────────── */

/**
 * 가려 둔 강사. 회원 가리기와 같이 **월 축이 없습니다**.
 * 목이 월별로 들고 있으면 "지난달 가린 강사가 이번 달에 돌아오는"
 * 잘못된 동작을 개발 중에 정상으로 오해하게 됩니다.
 */
let hiddenCoachIds = new Set<string>();

/**
 * 강사 가리기 / 다시 보기.
 * 회원 가리기와 같이 **멱등**입니다 — 이미 그 상태면 `changed: false` 로 200 입니다.
 */
export const mockSetCoachHidden = (
    coachId: string,
    hidden: boolean
): UpdateCoachVisibilityResponse => {
    const known = mockCoachRows.some((c) => c.coachId === coachId);
    /* 가릴 때만 "이 센터 강사인가"를 본다. 해제는 언제나 열려 있다 (서버와 같다) */
    if (hidden && !known) throw httpError(400, '이 센터에 연결된 적 없는 강사입니다.');

    const was = hiddenCoachIds.has(coachId);
    const next = new Set(hiddenCoachIds);
    if (hidden) next.add(coachId);
    else next.delete(coachId);
    hiddenCoachIds = next;

    return { coachId, hidden, changed: was !== hidden };
};

export const mockCoaches = (month: string): CoachesResponse => ({
    month,
    /* 서버는 가려진 강사도 응답에서 빼지 않는다. 표에서 감추는 것은 화면의 일이다 */
    coaches: mockCoachRows.map((c) => ({ ...c, hidden: hiddenCoachIds.has(c.coachId) })),
    closing: mockCoachClosing.map((c) => ({ ...c, hidden: hiddenCoachIds.has(c.coachId) })),
    centerAvg: {
        conversionPct: COACH_CENTER_AVG.conversionPct,
        closingRate: COACH_CENTER_AVG.closingRate,
        lessonsPerActiveMember: COACH_CENTER_AVG.lessonsPerActiveMember,
        sameDayCancel: COACH_CENTER_AVG.sameDayCancel,
        responseHours: COACH_CENTER_AVG.responseHours,
        journalPct: COACH_CENTER_AVG.journalPct,
        journalReadPct: COACH_CENTER_AVG.journalReadPct,
        noshowPct: COACH_CENTER_AVG.noshowPct,
        injuryPct: COACH_CENTER_AVG.injuryPct,
    },
    centerClosing: { ...CENTER_CLOSING },
});

/* ── ⑤ 지시 ──────────────────────────────────────────────── */

/**
 * 목 모드에서만 쓰는 메모리 저장소.
 * 새로고침하면 초기화된다 — 서버가 붙으면 사라질 임시 상태다.
 */
let orderStore: OrderDto[] = mockOrders.map((o) => ({
    orderId: o.orderId,
    memberId: o.orderId,
    memberName: o.memberName,
    coachId: mockCoachRows.find((c) => c.name === o.coachName)?.coachId ?? '',
    coachName: o.coachName,
    task: o.task,
    ownerNote: o.ownerNote,
    memberDraft: o.memberDraft,
    status: o.status,
    sentAt: o.sentAt,
    seenAt: o.seenAt,
    doneAt: o.doneAt,
    doneBy: o.doneBy,
    coachReport: o.coachReport,
    reopened: o.reopened,
    valueAmount: o.valueAmount,
    valueKind: o.valueKind,
    sentDayOffset: o.sentDayOffset,
}));

/**
 * 서버와 같은 규칙으로 거릅니다 — 기본 목록은 **정리된 건 전부**를 뺍니다
 * (`ARCHIVED` 확인 완료 · `RETRACTED` 철회).
 *
 * 목이 서버보다 관대하면, 목으로 만든 화면이 실서버에서 빈 목록이 됩니다.
 * 철회한 건이 목에서는 그냥 보이는데 실서버에서는 안 보이는 식입니다.
 */
export const mockOrderList = ({
    includeArchived = false,
    onlyRetracted = false,
}: { includeArchived?: boolean; onlyRetracted?: boolean } = {}): OrderDto[] => {
    /*
     * 서버와 같이 **정렬까지** 흉내냅니다. 목이 실서버와 다르게 정렬하면
     * 목으로 본 화면과 실제 화면의 맨 윗줄이 달라집니다 — 이 서랍에서 맨 위는
     * "방금 취소한 것"이라는 뜻이라 순서 자체가 정보입니다.
     */
    if (onlyRetracted) {
        return orderStore
            .filter((o) => o.status === 'RETRACTED')
            .slice()
            .sort((a, b) => (b.retractedAt ?? '').localeCompare(a.retractedAt ?? ''))
            .map((o) => ({ ...o }));
    }
    return orderStore
        .filter((o) => includeArchived || (o.status !== 'ARCHIVED' && o.status !== 'RETRACTED'))
        .map((o) => ({ ...o }));
};

export const mockCreateOrder = (body: CreateOrderRequest): OrderDto => {
    const created: OrderDto = {
        orderId: `o_mock_${orderStore.length + 1}_${dayjs().valueOf()}`,
        memberId: body.memberId,
        memberName: mockMemberCards.find((m) => m.memberId === body.memberId)?.name ?? '회원',
        coachId: body.coachId,
        coachName: mockCoachRows.find((c) => c.coachId === body.coachId)?.name ?? '강사',
        task: body.task,
        ownerNote: body.ownerNote,
        /*
         * 서버는 키를 안 보낸 요청만 브리핑 초안으로 채웁니다
         * (opsBriefing.service.resolveMemberDraft). 목에는 채울 초안이 없으므로
         * 받은 값만 눕혀서 저장합니다 — 공백만 남은 것은 글이 아니라 null 입니다.
         */
        memberDraft: body.memberDraft?.trim() || null,
        status: 'SENT',
        sentAt: dayjs().toISOString(),
        valueAmount: body.valueAmount,
        valueKind: body.valueKind,
        sentDayOffset: 0,
    };
    orderStore = [created, ...orderStore];
    return { ...created };
};

export const mockUpdateOrder = (orderId: string, body: UpdateOrderRequest): OrderDto => {
    orderStore = orderStore.map((o) => {
        if (o.orderId !== orderId) return o;
        if (body.status === 'DONE') {
            return {
                ...o,
                status: 'DONE',
                doneAt: dayjs().toISOString(),
                doneBy: 'OWNER',
                coachReport: o.coachReport ?? '강사 보고 없이 직접 완료 처리하셨습니다',
                reopened: false,
            };
        }
        if (body.status === 'ARCHIVED') return { ...o, status: 'ARCHIVED' };
        // 철회는 여기로 오지 않는다 — 타입에서 뺐고, 서버도 이 PATCH 로는 400 이다
        // 완료 해제 — 강사 보고는 남기고 다시 열림 표시만 붙인다
        const byCoach = o.doneBy === 'COACH';
        return {
            ...o,
            status: body.status,
            doneAt: undefined,
            doneBy: undefined,
            coachReport: byCoach ? o.coachReport : undefined,
            reopened: byCoach,
        };
    });
    return { ...orderStore.find((o) => o.orderId === orderId)! };
};

/**
 * 지시 철회.
 *
 * **서버의 두 갈래를 그대로 흉내냅니다.** 목이 더 관대하면 목으로 만든 흐름이
 * 실서버에서 400 으로 막힙니다 — 그 400 은 원장이 사유를 적는 자리라
 * 화면이 그 길을 한 번도 안 지나 보고 배포되면 안 됩니다.
 *
 *   회수  seenAt 이 없다 → 사유 불필요. 알림 없음(retractDelivery = null)
 *   통보  seenAt 이 있다 → **사유 필수**(없으면 400). 알림이 나가고 PENDING → DELIVERED
 */
export const mockRetractOrder = (orderId: string, reason?: string): OrderDto => {
    const target = orderStore.find((o) => o.orderId === orderId);
    if (!target) throw httpError(404, '해당 지시를 찾을 수 없습니다.');
    if (target.status === 'RETRACTED') throw httpError(409, '이미 철회한 지시입니다.');

    const trimmed = reason?.trim() || null;
    // 강사가 본 지시인가 — 갈래는 저장된 사실로만 정한다 (서버와 같다)
    const notifies = !!target.seenAt;
    if (notifies && !trimmed) {
        throw httpError(400, '강사가 이미 확인한 지시입니다. 철회 사유를 적어 주세요.');
    }

    const now = dayjs().toISOString();
    const retracted: OrderDto = {
        ...target,
        status: 'RETRACTED',
        retractedAt: now,
        retractNote: trimmed,
        /*
         * 회수 갈래는 null 입니다 — "안 보냈다"가 아니라 **"보낼 대상이 아니다"** 입니다.
         * 0 이나 'FAILED' 로 채우면 정상 동작이 실패처럼 보입니다.
         */
        retractDelivery: notifies ? 'DELIVERED' : null,
        retractDeliveredAt: notifies ? now : null,
        /*
         * doneAt / doneBy / coachReport 는 지우지 않습니다. 되돌리기(완료 해제)가
         * 그것들을 지우는 것과 정반대입니다 — 강사가 실제로 한 일을 원장의 정정이
         * 지울 수는 없습니다.
         */
    };
    orderStore = orderStore.map((o) => (o.orderId === orderId ? retracted : o));
    return { ...retracted };
};

/* ── ⑥ 지난 리포트 ───────────────────────────────────────── */

/**
 * 목 모드 저장소.
 * 서버가 없으면 체크가 재조회에서 되돌아가 기능이 고장난 것처럼 보인다.
 * 서버와 같은 수준으로 상태를 들고 있는다. 새로고침하면 초기화된다.
 */
let dayStore = mockDayReports.map((d) => ({
    ...d,
    highlights: d.highlights.map((h) => ({ ...h })),
}));

/** 지난 브리핑 항목을 뒤늦게 실행 처리 */
export const mockSetHistoryItemDone = (date: DateKst, index: number, done: boolean): void => {
    dayStore = dayStore.map((d) => {
        if (d.date !== date) return d;
        const highlights = d.highlights.map((h, i) => (i === index ? { ...h, done } : h));
        // 실행 건수도 같이 움직여야 히트맵 색과 실행률이 맞는다
        return { ...d, highlights, acted: highlights.filter((h) => h.done).length };
    });
};

/**
 * 목 모드 커버리지.
 *
 * 목에도 이걸 넣어 두지 않으면, 목으로 화면을 보는 동안에는 브리핑이 없던 날이
 * 다시 "이슈 없던 날"로 보입니다. 우리가 방금 고친 거짓말을 개발 중에는
 * 계속 보게 되는 셈이라, 목에서도 같은 규칙으로 만듭니다.
 *
 * 상태 판정은 서버와 같은 규칙입니다 — 문서가 있으면 PUBLISHED,
 * 첫 기록 이전이면 BEFORE_FIRST_RECORD, 그 이후인데 문서가 없으면 MISSING.
 */
const mockCoverage = (month: string): MonthCoverageDto => {
    const today = dayjs().format('YYYY-MM-DD');
    const start = dayjs(`${month}-01`);
    const dates = dayStore.map((d) => d.date).sort();
    const first = dates[0] ?? null;
    const last = dates[dates.length - 1] ?? null;
    const has = new Set(dates);

    const days = Array.from({ length: start.daysInMonth() }, (_, i) => {
        const date = start.add(i, 'day').format('YYYY-MM-DD');
        let state: OpsBriefingDayState;
        if (has.has(date)) state = 'PUBLISHED';
        else if (date > today) state = 'FUTURE';
        else if (date === today && dayjs().hour() < 12) state = 'PENDING';
        else if (!first) state = 'NO_RECORD_EVER';
        else if (date < first) state = 'BEFORE_FIRST_RECORD';
        else state = 'MISSING';
        return { date, state };
    });

    return { firstBriefingDate: first, lastBriefingDate: last, today, days };
};

export const mockHistory = (month: string): HistoryResponse => ({
    month,
    coverage: mockCoverage(month),
    days: dayStore.map((d) => ({
        ...d,
        /* 목 데이터에는 편성 기록이 없다. 모르는 것은 null 이다 */
        composition: null,
        highlights: d.highlights.map((h) => ({ ...h })),
    })),
    effect: {
        retainedWithAction: mockMonthlySummary.retainedWithAction,
        retainedWithoutAction: mockMonthlySummary.retainedWithoutAction,
        predictionAccuracy: mockMonthlySummary.predictionAccuracy,
        reportedWrong: mockMonthlySummary.reportedWrong,
    },
    insights: mockMonthlyInsights,
});
